import React, { useState, useEffect } from "react";
import { BattleState } from "../../State/State";
import { BattleStages } from "./types";
import { AnimationState } from "./useBattleAnimation";
import { useTypewriter } from "./useTypewriter";

interface BattleInfoPanelProps {
  battleStage: BattleStages;
  animationState: AnimationState;
  battleResult: BattleState | null;
  onClose?: () => void;
}

export const BattleInfoPanel: React.FC<BattleInfoPanelProps> = ({
  battleStage,
  animationState,
  battleResult,
  onClose,
}) => {
  const [showCloseButton, setShowCloseButton] = useState(false);

  const { displayedText, isComplete } = useTypewriter(
    animationState.showStory && battleResult ? battleResult.story || "" : "",
    2000
  );

  useEffect(() => {
    if (isComplete && animationState.showResult) {
      const timer = setTimeout(() => {
        setShowCloseButton(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setShowCloseButton(false);
    }
  }, [isComplete, animationState.showResult]);

  const getPanelContent = () => {
    if (animationState.showStory && battleResult) {
      return (
        <div className="text-center px-4">
          <p className="text-sm">{displayedText}</p>
          {isComplete && animationState.showResult && (
            <>
              <h2 className="text-xl mt-4 appear-from-opacity">
                {battleResult.result === "win" ? "You Win!" : "You Lose!"}
              </h2>
              {showCloseButton && (
                <div
                  className="mt-4 px-6 py-2 bg-black text-white appear-from-opacity select-none cursor-pointer"
                  onClick={onClose}
                >
                  Close
                </div>
              )}
            </>
          )}
        </div>
      );
    }
    if (animationState.showBattleAnimation) {
      return <h2 className="text-xl text-center">Battle in progress...</h2>;
    }
    if (battleStage === BattleStages.INITIALISING) {
      return <h2 className="text-xl text-center">Initialising...</h2>;
    }
    if (battleStage === BattleStages.CHOOSE_MAIN_ATTACKING_CARD) {
      return <h2 className="text-xl text-center">Choose Attacking Card</h2>;
    }
    if (battleStage === BattleStages.SELECT_SECONDARY_SIDE_CARD) {
      return <h2 className="text-xl text-center">Choose Secondary Card</h2>;
    }
    if (battleStage === BattleStages.SELECT_THIRD_SIDE_CARD) {
      return <h2 className="text-xl text-center">Choose Third Card</h2>;
    }
    if (battleStage === BattleStages.AWAITING_HAND) {
      return <h2 className="text-xl text-center">Awaiting Hand</h2>;
    }
    if (battleStage === BattleStages.AWAITING_RESULT) {
      return <h2 className="text-xl text-center">Awaiting Result</h2>;
    }
    if (battleStage === BattleStages.WIN) {
      return <h2 className="text-xl text-center">You Win!</h2>;
    }
    if (battleStage === BattleStages.LOSE) {
      return <h2 className="text-xl text-center">You Lose!</h2>;
    }
  };

  return (
    <div className="absolute top-[50%] w-[80%] left-[10%] translate-y-[-50%] antique flex items-center justify-center py-4 border border-black/20 outline outline-8 outline-white/20">
      {getPanelContent()}
    </div>
  );
};
